import React from "react";
import { FmMapObservationControls } from "./FmMapObservationControls";
import { FmInline, FmStack, FmText } from "./FmComposition";
import { FmLegend } from "./FmLegend";
import { FmSwitch } from "./FmSwitch";
import { FmSelect } from "./FmSelect";
import { FmAccordion } from "./FmAccordion";
import { FmBanner } from "./FmBanner";
import { FmButton } from "./FmButton";
import { mapLayerCapability } from "./FmMapLayerCapabilities";
import { FmMapViewportControls } from "./FmMapViewportControls";
import type {
  FmMapBasemapDescriptor,
  FmMapLayerDescriptor,
  FmMapLayerId,
  FmMapPanelDescriptor,
} from "./FmMapWorkspaceRoot.types";
import { resolveSpeciesColor } from "@/utils/colorSwatches";

type LegendPanelDescriptor = Extract<FmMapPanelDescriptor, { kind: "legend" }>;

interface MapWorkspaceLegendProps {
  panel: LegendPanelDescriptor;
  layers: readonly FmMapLayerDescriptor[];
  baseMaps: readonly FmMapBasemapDescriptor[];
  activeBaseMapId: FmMapBasemapDescriptor["id"];
  onLayerToggle: (layerId: FmMapLayerId, enabled: boolean) => void;
  onBaseMapSelect: (baseMapId: FmMapBasemapDescriptor["id"]) => void;
  onAction: (actionId: string) => void;
}

const MAPPING_STATUS_COLOR = {
  auto: "var(--fm-plot-auto)",
  auto_review: "var(--fm-plot-review)",
  manual: "var(--fm-plot-manual)",
  unmapped: "var(--fm-plot-unmapped)",
} as const;

const RiskAccessRead: React.FC<{
  layer: Extract<FmMapLayerDescriptor, { kind: "risk" }>;
  onAction: (actionId: string) => void;
}> = ({ layer, onAction }) => {
  if (!layer.visible) return null;
  if (layer.access.kind === "available") {
    return (
      <FmText size="caption" tone="muted">
        {layer.access.advisory}
      </FmText>
    );
  }
  if (layer.access.kind === "loading") {
    return <FmBanner tone="info" title={layer.access.label} />;
  }
  if (layer.access.kind === "error") {
    return <FmBanner tone="error" title={layer.access.message} />;
  }
  if (layer.access.kind === "unavailable") {
    return <FmBanner tone="info" title={layer.access.reason} />;
  }
  if (layer.access.kind === "not-evaluable") {
    return (
      <FmBanner
        tone="warning"
        title={layer.access.reason}
        data-testid="map-risk-not-evaluable"
      />
    );
  }
  const access = layer.access;
  return (
    <FmBanner
      tone="info"
      title={access.title}
      description={access.explanation}
      data-testid="sentry-locked-preview"
    >
      {/* The narrow map panel reads the unlock action beneath the explanation. */}
      <FmButton
        size="sm"
        variant="bordered"
        onClick={() => onAction(access.actionId)}
        data-testid="map-risk-access-action"
      >
        {access.actionLabel}
      </FmButton>
    </FmBanner>
  );
};

const ForestSemantics: React.FC<{
  semantics: LegendPanelDescriptor["semantics"];
}> = ({ semantics }) => (
  <FmAccordion
    plain
    data-testid="forest-semantics-disclosure"
    items={[
      {
        key: "forest-semantics",
        label: semantics.legendLabel,
        children: (
          <FmStack gap="md">
            {semantics.species.length > 0 ? (
              <section>
                <FmText
                  as="h3"
                  size="caption"
                  data-testid="forest-legend-species-label"
                >
                  {semantics.speciesLabel}
                </FmText>
                <FmLegend
                  entries={semantics.species.map((species) => ({
                    color: resolveSpeciesColor(species.code),
                    label: species.label,
                    testId: `forest-legend-species-${species.code}`,
                  }))}
                  direction="column"
                />
                <FmText size="caption" tone="muted">
                  {semantics.speciesExplanation}
                </FmText>
              </section>
            ) : null}
            <section>
              <FmText as="h3" size="caption" data-testid="forest-legend-volume-label">
                {semantics.volumeLabel}
              </FmText>
              <div
                aria-hidden="true"
                className="fm-legend-ramp"
                data-testid="forest-legend-volume-ramp"
              />
              <FmText size="caption" tone="muted">
                {semantics.volumeExplanation}
              </FmText>
            </section>
            <section>
              <FmText as="h3" size="caption" data-testid="forest-legend-maturity-label">
                {semantics.maturityLabel}
              </FmText>
              <FmInline gap="xs">
                <span
                  aria-hidden="true"
                  className="fm-legend-swatch--outline fm-legend-swatch--danger"
                  data-testid="forest-legend-maturity-swatch"
                />
                <FmText data-testid="forest-legend-maturity-value">
                  {semantics.maturityValueLabel}
                </FmText>
              </FmInline>
              <FmText size="caption" tone="muted">
                {semantics.maturityExplanation}
              </FmText>
            </section>
          </FmStack>
        ),
      },
    ]}
  />
);

export const MapWorkspaceLegend: React.FC<MapWorkspaceLegendProps> = ({
  panel,
  layers,
  baseMaps,
  activeBaseMapId,
  onLayerToggle,
  onBaseMapSelect,
  onAction,
}) => {
  const { semantics } = panel;
  return (
    <FmStack gap="md" data-testid="forest-legend-card">
      <FmStack gap="xs">
        <FmText as="h3" size="caption">
          {semantics.baseMapLabel}
        </FmText>
        <FmSelect<FmMapBasemapDescriptor["id"]>
          value={activeBaseMapId}
          options={baseMaps.map((baseMap) => ({
            value: baseMap.id,
            label: baseMap.label,
          }))}
          onChange={onBaseMapSelect}
          controlWidth="fill"
          ariaLabel={semantics.baseMapLabel}
          data-testid="map-basemap-select"
        />
      </FmStack>
      <FmMapViewportControls
        fitLabel={semantics.fitMapLabel}
        zoomInLabel={semantics.zoomInLabel}
        zoomOutLabel={semantics.zoomOutLabel}
      />
      <FmStack gap="xs">
        {panel.layerIds.map((id) => {
          const layer = layers.find((candidate) => candidate.id === id);
          return layer ? (
            <FmStack key={id} gap="xs">
              <FmInline align="center" justify="between" gap="sm">
                <FmText>{layer.label}</FmText>
                <FmSwitch
                  checked={layer.visible}
                  onChange={(enabled) => onLayerToggle(layer.id, enabled)}
                  ariaLabel={`${layer.label} · ${
                    layer.visible
                      ? semantics.visibleLayerLabel
                      : semantics.hiddenLayerLabel
                  }`}
                  data-testid={`map-layer-toggle-${layer.id}`}
                />
              </FmInline>
              {mapLayerCapability(layer).control === "risk-access" &&
              layer.kind === "risk" ? (
                <RiskAccessRead layer={layer} onAction={onAction} />
              ) : null}
            </FmStack>
          ) : null;
        })}
      </FmStack>
      {layers.some(
        (layer) =>
          layer.visible &&
          mapLayerCapability(layer).control === "radar-playback",
      ) ? (
        <FmMapObservationControls />
      ) : null}
      {semantics.featureLegend?.entries.length ? (
        <section>
          <FmText as="h3" size="caption">
            {semantics.featureLegend.title}
          </FmText>
          <FmLegend
            entries={semantics.featureLegend.entries.map((entry) => ({
              color: MAPPING_STATUS_COLOR[entry.status],
              label: entry.label,
              testId: `map-feature-legend-${entry.status}`,
            }))}
            direction="column"
          />
        </section>
      ) : null}
      <ForestSemantics semantics={semantics} />
    </FmStack>
  );
};
